import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import './ChatList.css';

function ChatList() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [userNicknames, setUserNicknames] = useState({}); // uid -> 最新ニックネーム
  const [loading, setLoading] = useState(true);

  const fetchChats = useCallback(async () => {
    try {
      const chatsQuery = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', currentUser.uid),
        orderBy('lastMessageAt', 'desc')
      );
      const snapshot = await getDocs(chatsQuery);
      const chatsData = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(chat => chat.status !== 'dissolved');
      setChats(chatsData);

      // チャットに関係する全ユーザーのニックネームをまとめて取得
      const uids = new Set();
      chatsData.forEach(chat => {
        chat.participants?.forEach(uid => uids.add(uid));
      });
      const nicknames = {};
      await Promise.all([...uids].map(async uid => {
        try {
          const userDoc = await getDoc(doc(db, 'users', uid));
          if (userDoc.exists()) {
            const d = userDoc.data();
            nicknames[uid] = d.nickname || d.displayName || '';
          }
        } catch (_) {}
      }));
      setUserNicknames(nicknames);
    } catch (err) {
      console.error('チャット一覧の取得に失敗しました:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    fetchChats();
  }, [currentUser, fetchChats]);

  if (loading) {
    return <div className="loading">読み込み中...</div>;
  }

  return (
    <div className="chatlist-container">
      <div className="chatlist-header">
        <h1>チャット一覧</h1>
      </div>

      {chats.length === 0 ? (
        <div className="chatlist-empty">
          <p>チャットはまだありません。</p>
          <p className="chatlist-empty-hint">募集に応募すると、主催者とチャットができます。</p>
          <Link to="/recruitments" className="btn btn-primary">募集を探す</Link>
        </div>
      ) : (
        <div className="chatlist">
          {chats.map(chat => {
            const otherUserId = chat.participants.find(uid => uid !== currentUser.uid);
            const otherName = chat.participantNames?.[otherUserId] || '相手';
            const lastMsg = chat.lastMessage || 'メッセージはまだありません';
            const lastAt = chat.lastMessageAt?.toDate
              ? chat.lastMessageAt.toDate().toLocaleDateString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
              : '';

            const recruitmentName = chat.recruitmentName || '';
            // 主催者の最新ニックネームを使用（変更に追従）
            const organizerId = chat.organizerId;
            const organizerName = organizerId
              ? (userNicknames[organizerId] || chat.participantNames?.[organizerId] || '主催者')
              : (userNicknames[otherUserId] || otherName);

            const isGroup = chat.type === 'group' || chat.id.startsWith('group_');
            const participantCount = chat.participants?.length ?? 0;

            return (
              <div
                key={chat.id}
                className="chat-item"
                onClick={() => {
                  if (isGroup) {
                    navigate(`/group-chat/${chat.id.replace('group_', '')}`);
                  } else {
                    navigate(`/chat/${chat.id}`);
                  }
                }}
              >
                <div className={`chat-avatar ${isGroup ? 'chat-avatar--group' : ''}`}>
                  {isGroup ? '👥' : (organizerName || '?').charAt(0)}
                </div>
                <div className="chat-info">
                  <div className="chat-top">
                    <span className="chat-name">
                      {isGroup ? (
                        <>
                          <span className="group-chat-badge">グループ</span>
                          {recruitmentName || '募集グループ'}
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: '4px' }}>
                            {participantCount}人
                          </span>
                        </>
                      ) : (
                        <>{recruitmentName ? `「${recruitmentName}」` : ''}主催者: {organizerName}</>
                      )}
                    </span>
                    {lastAt && <span className="chat-time">{lastAt}</span>}
                  </div>
                  <p className="chat-last-msg">{lastMsg}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ChatList;
